import { useEffect } from "react";
import type { PageProps } from "../pages/Page";
import type { CtmScriptProps, CtmVisitor, ScriptMetaData } from "./types";

export const getCtmScriptProps = (
  pageData: PageProps,
): CtmScriptProps | undefined =>
  pageData.scripts?.find(
    ({ metadata }: ScriptMetaData) => metadata.name === "comparethemarket",
  )?.props as CtmScriptProps;

/// Function which handles a) firing the startQuote event, and b) observing the questionnaire to fire emailDisclosed and endQuote events.
const beginObservingQuestionnaire = (affiliateId: string) => {
  if (window.tracking.Initialise === undefined) {
    console.log(
      "CTM tracking initialize function is not available. Tracking will not be enabled.",
    );
    return;
  }

  const visitor: CtmVisitor = window.tracking.Initialise(
    window.tracking.metaInfo.trackingUrl,
  );

  const questionnaire = document.getElementsByClassName(
    "mobius-questionnaire",
  )[0];

  // Journey Start
  if (window.tracking.isAvailable) {
    visitor.startQuote(window.tracking.metaInfo.productName, affiliateId);
  }

  // Monitor questionnaire until we they enter their email
  const emailObserver = new MutationObserver((_mutations, observer) => {
    const customerEmailAddress = questionnaire.querySelector(
      "input[id='customer[email_address]']",
    ) as HTMLInputElement;
    if (customerEmailAddress) {
      customerEmailAddress.addEventListener("blur", () => {
        if (window.tracking.isAvailable && customerEmailAddress.value.length) {
          visitor.emailDisclosed(
            window.tracking.metaInfo.productName,
            customerEmailAddress.value,
          );
          observer.disconnect(); // stop watching after we found the email input
        }
      });
    }
  });
  emailObserver.observe(questionnaire, { childList: true });

  // Monitor questionnaire until we they submit the questionnaire
  const assumptionsObserver = new MutationObserver((_mutations, observer) => {
    const assumptionSubmit = questionnaire.querySelector(
      ".mobius-submission__questionnaire-form",
    ) as HTMLButtonElement;
    if (assumptionSubmit) {
      assumptionSubmit.addEventListener("submit", () => {
        if (window.tracking.isAvailable) {
          visitor.endQuote(window.tracking.metaInfo.productName, affiliateId);
          observer.disconnect(); // stop watching after they hit submit
        }
      });
    }
  });
  assumptionsObserver.observe(questionnaire, { childList: true });
};

/*
  This is a recreation of the tracking snippet in the legacy journey for CTM
  https://github.com/simplybusiness/chopin/blob/master/app/views/shared/tracking/_comparethemarket.html.erb
*/
export const CtmTrackingProvider = ({
  scriptUrl,
  trackingUrl,
  application,
  affiliateId,
  productName,
  quickToQuote,
}: CtmScriptProps) => {
  const ctmTrackingElementId = "comparethemarket-tracking";

  useEffect(() => {
    if (!quickToQuote) return; // if we aren't in Q2Q don't load all this

    if (document.getElementById(ctmTrackingElementId) !== null) return; //Don't add the script again if it's already present

    /*
      Initial script block, sets what is apparently a global tracking variable
    */
    window.tracking = window.tracking || {};
    window.tracking.metaInfo = {
      application,
      trackingUrl: "//" + trackingUrl,
      page: document.title,
      productName,
      endQuoteFired: false,
    };

    /*
      Script block that pulls a script from CTM, this adds functions to the window.tracking object
    */
    const ctmUrlScript = document.createElement("script");
    ctmUrlScript.id = ctmTrackingElementId;
    ctmUrlScript.src = `//${scriptUrl}`;
    ctmUrlScript.async = true;

    ctmUrlScript.addEventListener("load", () => {
      //When the CTM script is loaded, begin observing the document until inner scripts have all run.
      //Once they do, we will fire startQuestionnaire and begin observing for email and submission

      if (window.tracking.Initialise !== undefined) {
        //The tracker is fully initialized. Don't bother watching the document for changes.
        beginObservingQuestionnaire(affiliateId);
        return;
      }

      //Observe all changes to the document until we find that the script has loaded
      //This is unfortunately required, because the CTM script loads its own child script asynchronously
      //See the comments and approach on https://github.com/simplybusiness/chopin/blob/master/app/views/shared/tracking/_comparethemarket.html.erb#L22
      const documentObserver = new MutationObserver((_mutations, observer) => {
        if (window.tracking.Initialise !== undefined) {
          observer.disconnect(); // stop watching after we confirmed the child scripts have run and the initialisation function is available
          beginObservingQuestionnaire(affiliateId);
        }
      });

      documentObserver.observe(document, { childList: true, subtree: true });
    }); // end onLoad of CTM script

    document.head.appendChild(ctmUrlScript);

    return () => {
      document.head.removeChild(ctmUrlScript);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // End CTM Snippet | we only want to run this effect once on page load

  return null;
};
